import { useEffect, useRef, useState } from 'react';
import useSWR from 'swr';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Stack,
  Typography,
} from '@mui/material';

import {
  getPlaygroundRun,
  type PlaygroundModel,
  type PlaygroundRun,
} from '@api/demand-playground';
import LetterColumn from './LetterColumn';
import {
  POLL_MS,
  errorText,
  isOffError,
  anyLetterRunning,
  isRunInProgress,
  modelLabel,
  pollInterval,
  promptsFile,
  saveTextFile,
} from '../utils/playground';

export const runKey = (runId: number) =>
  ['demand-playground-run', runId] as const;

const STATUS_TEXT: Record<string, string> = {
  running: 'Writing the letters',
  scoring: 'Checking the letters',
  done: 'Done',
  failed: 'Failed',
};

export const statusText = (status: string) => STATUS_TEXT[status] ?? status;

interface Props {
  runId: number;
  onRerun: (run: PlaygroundRun) => void | Promise<void>;
  /** For tests; the page polls every POLL_MS. */
  pollMs?: number;
  rerunning?: boolean;
  /** For the model's label; its id is shown without them. */
  models?: PlaygroundModel[];
}

/**
 * One saved run (ZLAYT-1660): the letter from the new prompts next to the
 * letter from the current prompts, each with its scores, polled every
 * 10 seconds until done or failed.
 */
function RunDetail({
  runId,
  onRerun,
  pollMs = POLL_MS,
  rerunning,
  models,
}: Props) {
  const errorRef = useRef<unknown>(null);
  const [copied, setCopied] = useState<'' | 'done' | 'failed'>('');
  const copiedTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(
    () => () => {
      if (copiedTimer.current) clearTimeout(copiedTimer.current);
    },
    []
  );
  const { data: run, error } = useSWR<PlaygroundRun>(
    runKey(runId),
    () => getPlaygroundRun(runId),
    {
      refreshInterval: (latest) => {
        if (errorRef.current && isOffError(errorRef.current)) return 0;
        return pollInterval(latest) ? pollMs : 0;
      },
      onErrorRetry: (err, _key, _config, revalidate, { retryCount }) => {
        if (isOffError(err) || retryCount >= 5) return;
        setTimeout(() => revalidate({ retryCount }), pollMs);
      },
    }
  );
  errorRef.current = error ?? null;

  if (!run) {
    if (error) {
      return (
        <Alert severity="error">
          {isOffError(error)
            ? 'This test run could not be found.'
            : errorText(error)}
        </Alert>
      );
    }
    return <CircularProgress size={20} />;
  }

  // Busy while the run or any of its letters is still going.
  const inProgress = isRunInProgress(run.status) || anyLetterRunning(run);
  const started = Date.parse(run.created_at);
  const startedText = Number.isFinite(started)
    ? ` · ${new Date(started).toLocaleString()}`
    : '';

  const download = () => {
    const file = promptsFile(run);
    saveTextFile(file.filename, file.json);
  };

  const copy = () => {
    const shown = (state: 'done' | 'failed') => {
      setCopied(state);
      if (copiedTimer.current) clearTimeout(copiedTimer.current);
      copiedTimer.current = setTimeout(() => setCopied(''), 4000);
    };
    if (!navigator.clipboard?.writeText) {
      shown('failed');
      return;
    }
    navigator.clipboard
      .writeText(promptsFile(run).json)
      .then(() => shown('done'))
      .catch(() => shown('failed'));
  };

  return (
    <Stack spacing={2}>
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        spacing={1}
        alignItems={{ xs: 'flex-start', md: 'center' }}
      >
        <Box sx={{ flexGrow: 1 }}>
          <Typography variant="h4" fontWeight={500}>
            {run.name || `Run ${run.id}`}
          </Typography>
          <Typography variant="body2" sx={{ color: '#475467' }}>
            {`${run.case?.label ?? ''} · ${modelLabel(run.writer_model, models)}${run.use_style_example ? ' · with the style example' : ''}${startedText}`}
          </Typography>
        </Box>
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          <Button size="small" variant="outlined" onClick={download}>
            Download prompts (JSON)
          </Button>
          <Button size="small" variant="outlined" onClick={copy}>
            Copy prompts
          </Button>
          <Button
            size="small"
            variant="outlined"
            disabled={inProgress || rerunning}
            onClick={() => onRerun(run)}
          >
            Run again
          </Button>
        </Stack>
      </Stack>
      <Typography
        role="status"
        variant="body2"
        sx={{ color: copied === 'failed' ? '#B42318' : '#027A48' }}
      >
        {copied === 'done' ? 'Copied' : ''}
        {copied === 'failed'
          ? 'Could not copy. Use Download prompts instead.'
          : ''}
      </Typography>

      <Stack direction="row" spacing={1} alignItems="center">
        {inProgress ? <CircularProgress size={16} /> : null}
        <Typography variant="body2" sx={{ color: '#475467' }}>
          {inProgress
            ? `${statusText(run.status)}. This takes about 2 to 4 minutes.`
            : statusText(run.status)}
        </Typography>
      </Stack>
      {run.status === 'failed' && run.error ? (
        <Alert severity="error">{run.error}</Alert>
      ) : null}
      {error && !isOffError(error) ? (
        <Alert severity="warning">{errorText(error)}</Alert>
      ) : null}

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
          gap: 2,
          alignItems: 'start',
        }}
      >
        <LetterColumn
          title="New prompts"
          testId="letter-new"
          letter={run.letters?.new ?? null}
          runInProgress={inProgress}
        />
        <LetterColumn
          title="Current prompts"
          testId="letter-current"
          letter={run.letters?.current ?? null}
          runInProgress={inProgress}
        />
      </Box>
    </Stack>
  );
}

export default RunDetail;
